import React from 'react';
import { CarIcon, XIcon } from 'lucide-react';
import type { ChoirEvent, Member } from '../../types';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { inputClass } from '../ui/Field';
import { useApp } from '../../contexts/AppContext';
import { fullName, transportModeLabel } from '../../utils/labels';
interface TransportBoardProps {
  event: ChoirEvent;
  udelezenci: Member[];
}
export function TransportBoard({
  event,
  udelezenci
}: TransportBoardProps) {
  const {
    transport,
    assignPassenger,
    setTransport,
    pushToast
  } = useApp();
  const zaDogodek = transport[event.id] ?? {};
  const vozniki = udelezenci.filter(m => zaDogodek[m.id]?.nacin === 'vozim');
  const potrebujejo = udelezenci.filter(m => zaDogodek[m.id]?.nacin === 'potrebujem');
  const brezPrijave = udelezenci.filter(m => !zaDogodek[m.id]);
  const skupnoMest = vozniki.reduce((s, m) => s + (zaDogodek[m.id]?.mesta ?? 0), 0);
  const brezVoznika = potrebujejo.filter(m => !zaDogodek[m.id]?.voznikId);
  function prostaMesta(voznikId: string): number {
    const mesta = zaDogodek[voznikId]?.mesta ?? 0;
    const zasedena = potrebujejo.filter(m => zaDogodek[m.id]?.voznikId === voznikId).length;
    return Math.max(mesta - zasedena, 0);
  }
  return <div className="space-y-4" data-id="element-164" data-mp="2dsk9xfbj9y1a">
      <Card className="p-5" data-id="element-165" data-mp="28zzhah4d5qzc">
        <div className="flex items-center gap-2" data-id="element-166" data-mp="28yodihax4j88">
          <CarIcon className="h-4 w-4 text-green-600" data-id="element-167" data-mp="28xwm7ioct7nr" />
          <h2 className="text-base font-semibold text-ink" data-id="element-168" data-mp="2nnc6cftdqeuh">Prevoz</h2>
        </div>
        <p className="mt-0.5 text-sm text-ink-muted" data-id="element-169" data-mp="2iqc3sevqnu84">
          Preprosta uskladitev prevozov med udeleženci — brez načrtovanja poti.
        </p>

        <div className="mt-4 grid gap-3 sm:grid-cols-4" data-id="element-170" data-mp="2wn7levq22tzs">
          {[{
          label: 'Voznikov',
          value: vozniki.length,
          cls: 'text-ink'
        }, {
          label: 'Prostih mest',
          value: skupnoMest,
          cls: 'text-ink'
        }, {
          label: 'Potrebuje prevoz',
          value: potrebujejo.length,
          cls: 'text-ink'
        }, {
          label: 'Brez voznika',
          value: brezVoznika.length,
          cls: brezVoznika.length ? 'text-amber-600' : 'text-green-600'
        }].map(s => <div key={s.label} className="rounded-lg bg-canvas px-3 py-2.5" data-id="element-171" data-mp="32ebmazj4xrw0">
              <p className={`text-xl font-semibold ${s.cls}`} data-id="element-172" data-mp="37cdhgitu5hnu">{s.value}</p>
              <p className="text-[12px] text-ink-muted" data-id="element-173" data-mp="3cbq3x2gsompw">{s.label}</p>
            </div>)}
        </div>
      </Card>

      <div className="grid gap-3 lg:grid-cols-2" data-id="element-174" data-mp="3ge399prmqim5">
        {vozniki.map(m => {
        const t = zaDogodek[m.id]!;
        const sopotniki = potrebujejo.filter(p => zaDogodek[p.id]?.voznikId === m.id);
        const prosto = prostaMesta(m.id);
        return <Card key={m.id} className="p-4" data-id="element-175" data-mp="3v27qsqey4o3c">
              <div className="flex items-start justify-between gap-3" data-id="element-176" data-mp="3v0wfm5j68e27">
                <div data-id="element-177" data-mp="3wg2lwkqblj6">
                  <h3 className="text-sm font-semibold text-ink" data-id="element-178" data-mp="3x7p8hwcm8xb">{fullName(m)}</h3>
                  <p className="mt-0.5 text-[13px] text-ink-muted" data-id="element-179" data-mp="7wqcvlfk0y3u">
                    {t.kraj ?? '—'}
                    {t.ura ? ` · odhod ${t.ura}` : ''}
                  </p>
                  {t.opomba && <p className="mt-1 text-[12px] text-ink-muted" data-id="element-180" data-mp="1odpbvo0y2v4q">{t.opomba}</p>}
                </div>
                <Badge className={prosto > 0 ? 'bg-green-50 text-green-700 border-green-200' : 'bg-slate-100 text-ink-muted border-line'} data-id="element-181" data-mp="1je2nqjjkvuh5">
                  {prosto > 0 ? `${prosto} prostih mest` : 'polno'}
                </Badge>
              </div>

              <ul className="mt-3 space-y-1.5" data-id="element-182" data-mp="22dqb6z1k9ui3">
                {sopotniki.map(p => <li key={p.id} className="flex items-center gap-2 rounded-lg border border-line px-2.5 py-1.5" data-id="element-183" data-mp="22bdlu0th4hv0">
                    <span className="min-w-0 flex-1 truncate text-[13px] text-ink" data-id="element-184" data-mp="2uvc590j5ccft">{fullName(p)}</span>
                    <button onClick={() => assignPassenger(event.id, p.id, null)} aria-label={`Odstrani ${fullName(p)} iz vozila`} className="rounded p-0.5 text-ink-muted transition-colors duration-150 ease-out hover:text-rose-600" data-id="element-185" data-mp="2q1zunubay662">
                      <XIcon className="h-3.5 w-3.5" data-id="element-186" data-mp="20pgc2iwp9skk" />
                    </button>
                  </li>)}
                {sopotniki.length === 0 && <li className="rounded-lg bg-canvas px-2.5 py-2 text-[12px] text-ink-muted" data-id="element-187" data-mp="2tcaqoz6u5xq4">
                    Brez sopotnikov.
                  </li>}
              </ul>

              {prosto > 0 && brezVoznika.length > 0 && <select aria-label={`Dodeli sopotnika k ${fullName(m)}`} value="" onChange={e => {
            if (e.target.value) assignPassenger(event.id, e.target.value, m.id);
          }} className={`${inputClass} mt-3`} data-id="element-188" data-mp="2okspqb4tbx55">
                  <option value="" data-id="element-189" data-mp="15l39hcytiq4u">Dodeli sopotnika …</option>
                  {brezVoznika.map(p => <option key={p.id} value={p.id} data-id="element-190" data-mp="10ms0t5k9elo7">
                      {fullName(p)}
                    </option>)}
                </select>}
            </Card>;
      })}
      </div>

      <Card className="p-5" data-id="element-191" data-mp="1afgxuvf2bx00">
        <h3 className="text-base font-semibold text-ink" data-id="element-192" data-mp="mn0f0ini40zn">Udeleženci brez urejenega prevoza</h3>
        <ul className="mt-3 divide-y divide-line" data-id="element-193" data-mp="hop92j0buamp">
          {[...brezVoznika, ...brezPrijave].map(m => {
          const t = zaDogodek[m.id];
          return <li key={m.id} className="flex flex-wrap items-center gap-3 py-2.5" data-id="element-194" data-mp="vlu8i05jcb8z">
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink" data-id="element-195" data-mp="qnsqlijuihz3">{fullName(m)}</span>
                <Badge className={t ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-slate-100 text-ink-muted border-line'} data-id="element-196" data-mp="275ggjioxgbwb">
                  {t ? transportModeLabel[t.nacin] : 'Brez prijave'}
                </Badge>
                {!t && <button onClick={() => {
              setTransport(event.id, m.id, {
                nacin: 'potrebujem'
              });
              pushToast(`${fullName(m)} je označen kot potrebuje prevoz.`);
            }} className="rounded-lg border border-line px-2.5 py-1.5 text-[13px] font-medium text-ink transition-colors duration-150 ease-out hover:bg-canvas" data-id="element-197" data-mp="18sudxqxoaeh7">
                    Označi „potrebuje prevoz“
                  </button>}
              </li>;
        })}
          {brezVoznika.length + brezPrijave.length === 0 && <li className="py-3 text-[13px] text-ink-muted" data-id="element-198" data-mp="3tfp94en6sjhj">Vsi udeleženci imajo urejen prevoz.</li>}
        </ul>
      </Card>
    </div>;
}